import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import React, { useEffect, useRef, useState } from "react";
import { CarouselContainer } from "../../style/components/Carousel";

/**
 * ComponentCarousel - A carousel that accepts an array of React components
 * @param {Object} props
 * @param {Array} props.components - Array of React components to display
 * @param {string} props.title - Optional carousel title
 * @param {number} props.itemWidth - Width of each carousel item in pixels
 * @param {number} props.gap - Gap between items in pixels
 * @param {number} props.scrollAmount - Amount to scroll on arrow click
 * @param {boolean} props.showArrows - Whether to show navigation arrows
 * @param {string} props.className - Optional class name for the container
 */

const ComponentCarousel = ({ components = [], title, itemWidth = 280, gap = 20, scrollAmount = 350, showArrows = true, className }) => {
  const [scrollPosition, setScrollPosition] = useState(0);
  const [maxScroll, setMaxScroll] = useState(0);
  const [visibleItems, setVisibleItems] = useState(0);
  const carouselRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (carouselRef.current && containerRef.current) {
      const width = containerRef.current.clientWidth;

      const scrollWidth = carouselRef.current.scrollWidth - carouselRef.current.clientWidth;
      setMaxScroll(scrollWidth > 0 ? scrollWidth : 0);

      const itemTotalWidth = itemWidth + gap;
      setVisibleItems(Math.floor(width / itemTotalWidth));
    }

    const handleResize = () => {
      if (carouselRef.current && containerRef.current) {
        const width = containerRef.current.clientWidth;

        const scrollWidth = carouselRef.current.scrollWidth - carouselRef.current.clientWidth;
        setMaxScroll(scrollWidth > 0 ? scrollWidth : 0);

        const itemTotalWidth = itemWidth + gap;
        setVisibleItems(Math.floor(width / itemTotalWidth));
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [components, itemWidth, gap]);

  const handleScroll = () => {
    if (carouselRef.current) {
      setScrollPosition(carouselRef.current.scrollLeft);
    }
  };

  useEffect(() => {
    const carouselElement = carouselRef.current;
    if (carouselElement) {
      carouselElement.addEventListener("scroll", handleScroll);
      return () => carouselElement.removeEventListener("scroll", handleScroll);
    }
  }, []);

  const scrollLeft = () => {
    if (carouselRef.current) {
      const newPosition = Math.max(0, scrollPosition - scrollAmount);
      carouselRef.current.scrollTo({
        left: newPosition,
        behavior: "smooth",
      });
      setScrollPosition(newPosition);
    }
  };

  const scrollRight = () => {
    if (carouselRef.current) {
      const newPosition = Math.min(maxScroll, scrollPosition + scrollAmount);
      carouselRef.current.scrollTo({
        left: newPosition,
        behavior: "smooth",
      });
      setScrollPosition(newPosition);
    }
  };

  const safeComponents = Array.isArray(components) ? components : [];

  const itemStyle = {
    width: `${itemWidth}px`,
    marginRight: `${gap}px`,
  };

  return (
    <CarouselContainer>
      <div ref={containerRef} className={`carousel-container ${className || ""}`}>
        {title && <div className="carousel-heading">{title}</div>}

        <div className="carousel-wrapper">
          {showArrows && scrollPosition > 0 && (
            <button className="carousel-nav-button carousel-nav-button-left" onClick={scrollLeft} aria-label="Scroll left">
              <ChevronLeft />
            </button>
          )}

          <div ref={carouselRef} className="carousel-scroll-container">
            {safeComponents.map((Component, index) => (
              <div key={index} className="carousel-item-container" style={itemStyle}>
                {Component}
              </div>
            ))}
          </div>

          {showArrows && scrollPosition < maxScroll && safeComponents.length > visibleItems && (
            <button className="carousel-nav-button carousel-nav-button-right" onClick={scrollRight} aria-label="Scroll right">
              <ChevronRight />
            </button>
          )}
        </div>
      </div>
    </CarouselContainer>
  );
};

export default ComponentCarousel;
